/**
 * Reading a governance document's detail record (`risk-subdomain/document`
 * API), shared by the document detail modal and the Webflow page builder
 * (lib/webflowDocuments/fieldData), so the page text says what the modal
 * shows. Pure and browser-safe.
 *
 * Coverage codes: 3 = Good, 2 = Minimal, anything else = none. Readers see
 * the names, never the numbers.
 */
import { SECTOR_FIELDS } from '../data/chartConfigs';

export type DocumentRecord = Record<string, unknown>;

export const SUBDOMAIN_IDS = [
  '1.1', '1.2', '1.3',
  '2.1', '2.2',
  '3.1', '3.2',
  '4.1', '4.2', '4.3',
  '5.1', '5.2',
  '6.1', '6.2', '6.3', '6.4', '6.5', '6.6',
  '7.1', '7.2', '7.3', '7.4', '7.5', '7.6',
];

export const SUBDOMAIN_NAMES: Record<string, string> = {
  '1.1': 'Unfair discrimination and misrepresentation',
  '1.2': 'Exposure to toxic content',
  '1.3': 'Unequal performance across groups',
  '2.1': 'Compromise of privacy',
  '2.2': 'AI system security vulnerabilities',
  '3.1': 'False or misleading information',
  '3.2': 'Pollution of information ecosystem',
  '4.1': 'Disinformation, surveillance, and influence at scale',
  '4.2': 'Cyberattacks, weapon development or use',
  '4.3': 'Fraud, scams, and targeted manipulation',
  '5.1': 'Overreliance and unsafe use',
  '5.2': 'Loss of human agency and autonomy',
  '6.1': 'Power centralization and unfair distribution',
  '6.2': 'Increased inequality and decline in employment',
  '6.3': 'Economic and cultural devaluation of human effort',
  '6.4': 'Competitive dynamics',
  '6.5': 'Governance failure',
  '6.6': 'Environmental harm',
  '7.1': 'AI pursuing its own goals',
  '7.2': 'AI possessing dangerous capabilities',
  '7.3': 'Lack of capability or robustness',
  '7.4': 'Lack of transparency or interpretability',
  '7.5': 'AI welfare and rights',
  '7.6': 'Multi-agent risks',
};

export const OECD_FRAMEWORK_URL =
  'https://www.oecd.org/content/dam/oecd/en/publications/reports/2022/02/oecd-framework-for-the-classification-of-ai-systems_336a8b57/cb6d9eca-en.pdf';

/** What Good and Minimal coverage mean, for `subject` ('risk subdomain' or 'sector'). */
export function coverageDefinition(level: 'good' | 'minimal', subject: string): string {
  return level === 'good'
    ? `Comprehensive governance measures or mitigations with clear procedures relating to the ${subject} are explicitly described.`
    : `Brief mention but no specific mitigations, recommendations or governance measures relating to the ${subject} are described.`;
}

export interface CoverageGroups {
  good: string[];
  minimal: string[];
}

export interface ActorGroup {
  label: string;
  types: string[];
  entities: string;
  summary: string;
}

/** The first of `keys` present on the record (null and undefined count as absent), as a string. */
function pick(doc: DocumentRecord, ...keys: string[]): string {
  for (const k of keys) {
    const v = doc[k];
    if (v != null) return String(v);
  }
  return '';
}

/** Parse a JSON array string or comma-separated string into string[] */
export function parseArrayField(value: string | undefined): string[] {
  if (!value) return [];
  const trimmed = value.trim();
  if (!trimmed) return [];

  // Try JSON parse first (for array fields serialised as JSON)
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch { /* fall through */ }
  }

  // Comma-separated fallback
  return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
}

export function groupSubdomainCoverage(doc: DocumentRecord): CoverageGroups {
  const good: string[] = [];
  const minimal: string[] = [];

  for (const id of SUBDOMAIN_IDS) {
    const raw = Number(pick(doc, `subdomain_${id.replace('.', '_')}_coverage`).trim());
    const label = SUBDOMAIN_NAMES[id] ?? id;
    if (raw === 3) good.push(label);
    else if (raw === 2) minimal.push(label);
  }

  return { good, minimal };
}

export function groupSectorCoverage(doc: DocumentRecord): CoverageGroups {
  const good: string[] = [];
  const minimal: string[] = [];

  for (const sf of SECTOR_FIELDS) {
    const raw = Number(pick(doc, sf.id).trim());
    if (raw === 3) good.push(sf.label);
    else if (raw === 2) minimal.push(sf.label);
  }

  return { good, minimal };
}

export interface DocumentFields {
  title: string;
  shortSummary: string;
  linkToDoc: string;
  jurisdiction: string;
  authority: string;
  parentAuthority: string;
  collections: string;
  recentActivity: string;
  recentActivityDate: string;
  legislativeStatus: string;
  legislativeStatusSummary: string;
  actorGroups: ActorGroup[];
  lifecycleStages: string[];
  aiLifecycleSummary: string;
  technicalScope: string[];
  technicalScopeSummary: string;
  riskCoverageSummary: string;
  sectorsSummary: string;
  appliesTo: string[];
  subdomainGroups: CoverageGroups;
  sectorGroups: CoverageGroups;
}

/** Every value the detail modal shows, read from the record (empty for a null record). */
export function readDocumentFields(document: DocumentRecord | null): DocumentFields {
  const doc = document ?? {};
  const arr = (...keys: string[]) => (document ? parseArrayField(pick(doc, ...keys) || undefined) : []);
  const actor = (label: string, role: string, title: string): ActorGroup => ({
    label,
    types: arr(`${role}Types`, `${role}_types`),
    entities: pick(doc, `${role}Entities`, `${title} Entities`),
    summary: pick(doc, `${role}Summary`, `${title} Summary`),
  });

  return {
    title: pick(doc, 'officialName', 'Official name'),
    shortSummary: pick(doc, 'shortSummary', 'Short summary'),
    linkToDoc: pick(doc, 'linkToDoc', 'Link to doc'),
    jurisdiction: pick(doc, 'jurisdiction'),
    authority: pick(doc, 'authority', 'Authority'),
    parentAuthority: pick(doc, 'parentAuthority', 'parent_authority'),
    collections: pick(doc, 'collections'),
    recentActivity: pick(doc, 'activity', 'Most recent activity'),
    recentActivityDate: pick(doc, 'activityDate', 'Most recent activity date'),
    legislativeStatus: pick(doc, 'legislativeStatus', 'legislative_primary_category'),
    legislativeStatusSummary: pick(doc, 'legislativeStatusSummary', 'Legislative Status Summary'),
    actorGroups: [
      actor('Proposer', 'proposer', 'Proposer'),
      actor('Target', 'target', 'Target'),
      actor('Enforcer', 'enforcer', 'Enforcer'),
      actor('Monitor', 'monitor', 'Monitor'),
    ],
    lifecycleStages: arr('lifecycleStage'),
    aiLifecycleSummary: pick(doc, 'aiLifecycleSummary', 'AI Lifecycle Summary'),
    technicalScope: arr('technicalScope'),
    technicalScopeSummary: pick(doc, 'technicalScopeSummary', 'Technical Scope Summary'),
    riskCoverageSummary: pick(doc, 'riskCoverageSummary', 'Risk Coverage Summary'),
    sectorsSummary: pick(doc, 'sectorsSummary', 'Sectors Summary'),
    appliesTo: arr('appliesTo'),
    subdomainGroups: document ? groupSubdomainCoverage(doc) : { good: [], minimal: [] },
    sectorGroups: document ? groupSectorCoverage(doc) : { good: [], minimal: [] },
  };
}
